import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { SeatCharacteristics } from '@fcom/dapi/interfaces/cart.interface';
import { ServiceCatalogSelections, TravelerService } from '@fcom/dapi/interfaces/services.interface';
import { Category } from '@fcom/dapi/api/models/category';
import { PreciseNumber } from '@fcom/core/utils/precise-number';

import { seatSelections } from './seat-map.selector';
import { serviceSelectionsForCategory } from './services.selector';
import { accumulateTieredPrice, sumAmounts } from '../../utils/amount.utils';
import { CommonBookingFeatureState } from '../store.interface';
import { includedServiceQuantitiesPerFragmentAndPaxSelector } from './order.selector';
import { SEAT_BUNDLE_CATEGORIES } from '../../modules/ancillaries/utils/category.utils';

/**
 * Returns the cost of all selected seats, excluding the ones that belong to bundles (no seatNumber), are included
 * in tickets, have already been paid, or for some other reason shouldn't be included in the ancillary subtotal flows.
 */
const nonBundledSeatSubTotalForJourneySelector = createSelector(seatSelections, (seatSelectionsByFlightAndPax) =>
  Object.values(seatSelectionsByFlightAndPax || {})
    .flat()
    .filter(seatServiceShouldCountTowardsSubTotal)
    .map((seatService) => serviceSubTotal(seatService))
    .reduce(sumAmounts, null)
);

/**
 * Returns the amount to be shown in a subtotal when the user is in some ancillary subflow of a given category.
 * Breaks down bundles into subcategories, determines which bundles are relevant for which subflows, excludes selected
 * services that have been paid or are included in the tickets, etc.
 */
export const nonSeatAncillarySubTotalForJourneySelector = (
  category: Category
): MemoizedSelector<CommonBookingFeatureState, Amount | null> =>
  createSelector(
    nonBundledSeatSubTotalForJourneySelector,
    serviceSelectionsForCategory<TravelerService | TravelerService[]>(category),
    includedServiceQuantitiesPerFragmentAndPaxSelector(category),
    (nonBundledSeatsSubTotal, otherServiceSelections, alreadyBoughtByFragmentPaxVariant) => {
      if (category === Category.SEAT) {
        return nonBundledSeatsSubTotal;
      }

      const subTotalForCategory: Amount | null = nonSeatAncillarySubTotalFromSelections(
        otherServiceSelections,
        alreadyBoughtByFragmentPaxVariant
      );

      if (SEAT_BUNDLE_CATEGORIES.includes(category)) {
        return sumAmounts(nonBundledSeatsSubTotal, subTotalForCategory);
      }

      return subTotalForCategory;
    }
  );

/**
 * Sums the subtotal for non-seat services in a category, applying tiered pricing per fragment + passenger.
 *
 * Tiered prices depend on how many of the variant were *already bought* (from the order's `included` services):
 * those items consume the earlier tier slots, so the newly selected items are priced at the later tiers. This
 * mirrors the `alreadyBoughtServiceVariantsAmount` handling in CommonProductSelectionComponent.
 */
const nonSeatAncillarySubTotalFromSelections = (
  selections: ServiceCatalogSelections<TravelerService | TravelerService[]>,
  alreadyBoughtByFragmentPaxVariant: Record<string, Record<string, Record<string, number>>> = {}
): Amount | null => {
  const amounts: Amount[] = [];

  Object.entries(selections || {}).forEach(([fragmentId, selectionsByPax]) => {
    Object.entries(selectionsByPax || {}).forEach(([paxId, servicesForPax]) => {
      const services: TravelerService[] = ([] as TravelerService[]).concat(servicesForPax).flat(2);
      const alreadyBoughtByVariant = alreadyBoughtByFragmentPaxVariant?.[fragmentId]?.[paxId] ?? {};

      services
        .filter(nonSeatServiceShouldCountTowardsSubTotal)
        .forEach((service) => amounts.push(serviceSubTotal(service, alreadyBoughtByVariant[service.variant] ?? 0)));
    });
  });

  return amounts.reduce(sumAmounts, null);
};

/**
 * As nonSeatServiceShouldCountTowardsSubTotal but also requires seatNumber
 */
const seatServiceShouldCountTowardsSubTotal = (service: TravelerService) =>
  !!service?.seatNumber &&
  !service.seatCharacteristics?.some((s) => s === SeatCharacteristics.SYSTEM) &&
  nonSeatServiceShouldCountTowardsSubTotal(service);

/**
 * Filters out services which are included in ticket, have already been paid, lack price info, etc.
 */
const nonSeatServiceShouldCountTowardsSubTotal = (service: TravelerService) =>
  service &&
  service.quantity > 0 &&
  !service.includedInTicketType &&
  !service.includedInTierBenefit &&
  !!service.pricePerItem?.currencyCode &&
  !service.paid;

/**
 * Multiply amount by quantity
 */
const multiplyAmount = (amount: Amount, quantity: number): Amount => {
  if (quantity === 1) {
    return amount;
  }
  return {
    ...amount,
    amount: PreciseNumber.fromString(amount?.amount ?? '0')
      .multiply(PreciseNumber.from(quantity, 0))
      .toString(),
  };
};

/**
 * Calculates the subtotal for a single service selection, applying tiered pricing when present.
 *
 * Delegates to the shared `accumulateTieredPrice` (also used by CommonProductSelectionComponent's
 * `calculateCurrentTierPricedServiceAccumulatePrice`): each tier provides a price for up to `maxNumberItems`
 * items, and items already bought (`alreadyBoughtAmount`) consume the earlier tier slots first so the newly
 * selected `quantity` is charged at the later tiers.
 */
export const serviceSubTotal = (service: TravelerService, alreadyBoughtAmount = 0): Amount => {
  if (!service?.tieredPricePerItem?.length) {
    return multiplyAmount(service.pricePerItem, service.quantity);
  }

  return accumulateTieredPrice(
    service.tieredPricePerItem.map(({ pricePerItem, maxNumberItems }) => ({
      price: pricePerItem,
      maxNumberItems,
    })),
    service.quantity,
    alreadyBoughtAmount
  ).total;
};
